import React, { useEffect, useState, Fragment, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Grid } from '@mui/material';

import { Button, Input, Select, Form, Accordion } from 'Components';
import ModalComponent, { ModalBody, ModalFooter } from 'Components/Modal';
import { NotificationManager } from 'Components/Notifications';

import {
    fleetModalOutputsOpen,
    fleetModalOutputsGet,
    fleetModalOutputsSave,
    fleetModalBulkOutputsSendCommand,
    clearRedux
} from 'Redux/actions'

const ModalOutputs = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const fleetOutputsForm = useRef();

    const open = useSelector(state => state.vehicleModalOuputsRedux.open);
    const id = useSelector(state => state.vehicleModalOuputsRedux.id);
    const unit = useSelector(state => state.vehicleModalOuputsRedux.unit);
    const loadingUnit = useSelector(state => state.vehicleModalOuputsRedux.loadingUnit);
    const loading = useSelector(state => state.vehicleModalOuputsRedux.loading);
    const response = useSelector(state => state.vehicleModalOuputsRedux.response);

    const loadingCommand = useSelector(state => state.fleetModalBulkOutputsRedux.loading);
    const responseCommand = useSelector(state => state.fleetModalBulkOutputsRedux.response);

    const [formData, setFormData] = useState({ outputs: [] });
    const [outputsCounter, setOutputsCounter] = useState([]);
    const selectOutputOptions = [
        { type: "Toggle", label: messages["unit_toggle_options"] }
    ];

    if (unit?.type != "queclink") {
        selectOutputOptions.push({ type: "Pulse", label: messages["unit_pulse_options"] });
    }

    selectOutputOptions.push({ type: "singlePulse", label: messages["unit_pulse_single_option"] });

    useEffect(() => {
        if (open && id) {
            dispatch(fleetModalOutputsGet(id));
        }
    }, [id, open]);

    useEffect(() => {
        if (open && unit) {
            if (unit?.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['error'],
                    3000,
                    null,
                    null,
                    'warning',
                    null
                );
            } else {
                let sOutputs = [];
                if (unit.supportedOutputs > 0) {
                    let unitOutputs = unit?.outputs || [];
                    let newOutputs = [];
                    for (let i = 0; i < unit.supportedOutputs; i++) {
                        sOutputs.push(i + 1);
                        let o = unitOutputs?.find(uo => uo.position === (i + 1));
                        if (o) {
                            newOutputs.push(o);
                        } else {
                            newOutputs.push({})
                        }
                    }

                    setOutputsCounter(sOutputs);
                    setFormData({ ...formData, outputs: newOutputs || [] })
                }
            }
        }
    }, [unit, open]);

    useEffect(() => {
        if (open && response) {
            if (response?.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['error'],
                    3000,
                    null,
                    null,
                    'warning',
                    null
                );

                dispatch(clearRedux("FLEET_MODAL_OUTPUTS", { response: null }));
            } else {
                NotificationManager.success(
                    messages['fleet_validation_outputs_message'],
                    messages['fleet_validation_outputs_title'],
                    3000,
                    null,
                    null,
                    'warning',
                    null
                );

                dispatch(clearRedux("FLEET_MODAL_OUTPUTS", { response: null }));
                handleClose();
            }
        }
    }, [response, open]);

    useEffect(() => {
        if (open && responseCommand) {
            if (responseCommand?.error) {
                NotificationManager.error(
                    messages['errorMessage'],
                    messages['error'],
                    3000,
                    null,
                    null,
                    'warning',
                    null
                );
            } else {
                NotificationManager.success(
                    messages["fleetModalBulkOutputs_commandSendSuccess"],
                    messages["success"],
                    3000,
                    null,
                    null,
                    "warning",
                    null
                );
            }

            dispatch(clearRedux("FLEET_MODAL_BULK_OUTPUTS", { response: null }));
        }

    }, [responseCommand, open]);


    const onSubmit = useCallback(() => {
        dispatch(fleetModalOutputsSave({
            toCreate: formData?.outputs?.filter(o => o?.name && o?.type) || [],
            toDelete: unit?.outputs || []
        }));
    });

    const sendCommand = useCallback((action, output) => {
        const command = {
            via: "SMS",
            message: {
                cannedCommand: {
                    commandKey: `output${output?.position}`,
                    action: action
                }
            },
            description: `${action} output ${output?.position}`,
            to: [{ entityName: "Unit", entityId: unit?.id }]
        };
        
        dispatch(fleetModalBulkOutputsSendCommand(command));
    });

    const handleClose = useCallback(() => {
        setOutputsCounter([]);
        dispatch(fleetModalOutputsOpen(false));
        dispatch(clearRedux("FLEET_MODAL_OUTPUTS"));
    });

    return (
        <ModalComponent
            open={open}
            handleClose={handleClose}
            title={(unit && (unit.label && unit.label.length > 28 ? unit.label.substring(0, 25) + "..." : unit.label)) || messages['fleet_inputs_modal_title']}
            loading={loadingUnit || loading || loadingCommand}
            position="right"
            size="small"
            style={{ overflow: 'hidden' }}
            className="overflow-hidden"
        >
            <ModalBody
                style={{
                    overflow: 'hidden',
                    marginBottom: 0
                }}
            >
                <div
                    style={{
                        padding: "0px 24px 70px 24px",
                        height: "calc(100vh - 225px)",
                        overflowX: 'auto'
                    }}
                >
                    <h3>{messages['fleet_inputs_modal_title']}</h3>

                    <Form
                        ref={fleetOutputsForm}
                        onValidSubmit={onSubmit}
                    >
                        {outputsCounter.map((i) => {
                            let output = {};
                            if (formData?.outputs && formData?.outputs?.length) {
                                output = formData?.outputs?.find(o => o?.position === i);
                            }

                            return (
                                <Accordion
                                    key={i}
                                    defaultExpanded
                                    title={`${messages['fleetModalOutput_label']} ${i}`}
                                    className="AccordionContainer-title"
                                >
                                    <div
                                        style={{
                                            width: "100%",
                                            marginTop: 15
                                        }}
                                    >
                                        <div>
                                            <Input
                                                id={"output-label-" + i}
                                                type="text"
                                                label={messages["fleetModalOutput_title"]}
                                                placeholder={messages["fleetModalOutput_title"]}
                                                maxLength={25}
                                                value={output?.name || ""}
                                                onChange={(value) => {
                                                    let copyOutputs = JSON.parse(JSON.stringify(formData.outputs));
                                                    copyOutputs[i - 1] = { ...copyOutputs[i - 1], name: value, position: i, state: "Off", unitId: unit?.id };
                                                    setFormData({ ...formData, outputs: copyOutputs });
                                                }}
                                                required={output?.type}
                                                addRequiredLabel
                                                requiredMessage={messages["required_field"]}
                                            />
                                        </div>

                                        <div>
                                            <Select
                                                id={"output-type-" + i}
                                                label={messages["unit_settings_type"]}
                                                placeholder={messages["unit_settings_type"]}
                                                options={selectOutputOptions}
                                                textField="label"
                                                valueField="type"
                                                value={
                                                    (output && selectOutputOptions.filter((option) => option.type === output?.type))
                                                        ? selectOutputOptions.filter((option) => option.type === output?.type)[0]
                                                        : null
                                                }
                                                onChange={(value) => {
                                                    let copyOutputs = JSON.parse(JSON.stringify(formData.outputs));
                                                    copyOutputs[i - 1] = { ...copyOutputs[i - 1], type: value?.type, position: i, state: "Off", unitId: unit?.id };
                                                    setFormData({ ...formData, outputs: copyOutputs });
                                                }}
                                                isClearable
                                                required={formData?.outputs && formData?.outputs[i - 1]?.name}
                                                addRequiredLabel
                                                requiredMessage={messages["required_field"]}
                                            />
                                        </div>

                                        <div className='text-right' style={{ marginTop: 10 }}>
                                            {
                                                (output?.type === "Toggle") &&
                                                <Fragment>
                                                    <Button
                                                        disabled={loadingUnit || loading}
                                                        onClick={(e) => {
                                                            if (e) e.preventDefault();
                                                            sendCommand("enable", output);
                                                        }}
                                                        className="btn-black btn-sm"
                                                        style={{
                                                            marginLeft: 5,
                                                            marginRight: 0
                                                        }}
                                                    >
                                                        {messages['fleet_send_on']}
                                                    </Button>

                                                    <Button
                                                        disabled={loadingUnit || loading}
                                                        onClick={(e) => {
                                                            if (e) e.preventDefault();
                                                            sendCommand("disable", output);
                                                        }}
                                                        className="btn-black btn-sm"
                                                        style={{
                                                            marginLeft: 5,
                                                            marginRight: 0
                                                        }}
                                                    >
                                                        {messages['fleet_send_off']}
                                                    </Button>
                                                </Fragment>
                                            }

                                            {
                                                (output?.type === "Pulse"  || output?.type ==="singlePulse") &&
                                                <Button
                                                    disabled={loadingUnit || loading}
                                                    onClick={(e) => {
                                                        if (e) e.preventDefault();
                                                        sendCommand(output?.type, output);
                                                    }}
                                                    className="btn-black btn-sm"
                                                    style={{
                                                        marginLeft: 5,
                                                        marginRight: 0
                                                    }}
                                                >
                                                    {messages['fleet_send_pulse']}
                                                </Button>
                                            }
                                        </div>
                                    </div>
                                </Accordion>
                            )
                        })}
                    </Form>
                </div>
            </ModalBody>

            <ModalFooter style={{ boxShadow: "2px 2px 2px 1px rgb(2 0 0 / 94%)" }}>
                <Fragment>
                    <Button
                        onClick={handleClose}
                        className="btn-cancel"
                        disabled={loadingUnit || loading}
                    >
                        {messages['cancel']}
                    </Button>

                    <Button
                        disabled={loadingUnit || loading}
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            fleetOutputsForm.current.submit();
                        }}
                        className="btn-blue"
                    >
                        {messages['save']}
                    </Button>
                </Fragment>
            </ModalFooter>
        </ModalComponent >
    );
};


export default ModalOutputs;
